import { Close, KeyboardArrowDown, KeyboardReturnRounded, Notifications, Payment } from "@mui/icons-material";
import { Box, Button, CircularProgress, Menu, MenuItem, Typography, Tooltip, Divider, IconButton, ListItemIcon, ListItemText } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useModals } from "../../context/ModalContext";
import { useFloatingBubbles } from "../../context/floating-bubbles/FloatingBubblesContext";
import { selectCurrentCompany, selectUserCompanies } from "../../redux/features/user/selectors/userState.selectors";
import { getBasePath } from "../../utils";
import { useSwitchActiveCompany } from "../shared/hooks/useSwitchActiveCompany";
import { colors } from "../ui/colors";
import { useDialog } from "../ui/dialogs/useDialog";
import { CompanyAvatar, EditableCompanyAvatar } from "./CompanyAvatar";
import { NOTIFICATION_COUNT_BY_COMPANY_QUERY_KEY } from "./SidebarConnect/hook/useNotificationCountByCompany";
import { useOptionalChatDock } from "../chat-dock";
import {
  ENotificationTargetKind,
  getNotificationChatMeta,
  getNotificationChatTarget,
  getNotificationCompanyId,
  getInboundMessageSenderCompany,
  getNotificationDisplayMessage,
  getNotificationTarget,
  hasNotificationActionFor,
  isInboundMessageNotification,
  NOTIFICATION_ICONS,
  triggerNotificationTarget,
} from "../notifications/notificationHelpers";
import {
  consumeNotificationReturnState,
  getModalKeysByStackOrder,
  getNotificationReturnState,
  saveNotificationReturnState,
} from "../notifications/notificationReturnState";
import { getMyNotifications, markAsSeenAllMyNotifications, updateMyNotificatioById } from "../../utils";
import { BaseModalComponent } from "./BaseModalComponent";
import SettingsCompany from "./SettingCompany";

// ─── Sub-components ───────────────────────────────────────────────────────────

const ActiveBadge = () => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.75,
      px: 1,
      height: 24,
      borderRadius: "999px",
      bgcolor: "rgba(251,142,0,0.10)",
      border: "1px solid rgba(251,142,0,0.22)",
    }}
  >
    <Box sx={{ width: 6, height: 6, borderRadius: "999px", bgcolor: colors.orange.base }} />
    <Typography sx={{ fontWeight: 1000, fontSize: 11, color: colors.orange.base }}>Current</Typography>
  </Box>
);

const formatRoleLabel = (role) => {
  if (role?.type === "business") return "Owner";
  return role?.name || role?.type || "";
};

const CompanyMenuItem = ({ membership, isActive, isPending, onSelect }) => {
  const location = membership.company?.state || membership.company?.country || "";
  const role = formatRoleLabel(membership.userRole?.role);

  return (
    <MenuItem
      key={membership.companyId}
      disabled={isActive || isPending}
      onClick={() => onSelect(membership.companyId)}
      sx={{
        position: "relative",
        mx: 0.75,
        my: 0.25,
        borderRadius: "12px",
        alignItems: "center",
        gap: 1,
        pl: isActive ? 1.75 : 1,
        "&.Mui-disabled": { opacity: 0.75 },
        "&:hover": { background: "rgba(251,142,0,0.06)" },
        ...(isActive && {
          "&::before": {
            content: '""',
            position: "absolute",
            left: 4,
            top: "50%",
            transform: "translateY(-50%)",
            width: 4,
            height: 34,
            borderRadius: "999px",
            bgcolor: colors.orange.base,
          },
        }),
      }}
    >
      <CompanyAvatar company={membership.company} size={36} />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography noWrap sx={{ fontWeight: 950, fontSize: 13, color: colors.gray.base }}>
          {membership.company?.businessName}
        </Typography>
        {!!(role || location) && (
          <Typography noWrap sx={{ fontWeight: 700, fontSize: 11, color: colors.gray?.[400] ?? "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.55)" }}>
            {!!role && (
              <Box component="span" sx={{ color: isActive ? colors.orange.base : "inherit" }}>
                {role}
              </Box>
            )}
            {!!role && !!location && " · "}
            {!!location && <Box component="span">{location}</Box>}
          </Typography>
        )}
      </Box>

      {isPending && !isActive ? <CircularProgress size={16} /> : isActive ? <ActiveBadge /> : null}
    </MenuItem>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

export const CompanySwitcher = ({ sx, isExpanded = true, notificationsCount = 0, onNotificationsClick }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { modals } = useModals();
  const { openCalls } = useFloatingBubbles();
  const chatDock = useOptionalChatDock();
  const { switchActiveCompany, isSwitchingCompany } = useSwitchActiveCompany();
  const { showDialog, DialogComponent } = useDialog();

  const companies = useSelector(selectUserCompanies);
  const currentCompany = useSelector(selectCurrentCompany);
  const activeCompanyId = useSelector((state) => state.userState.activeCompanyId ?? state.userState.company?.id);
  const userState = useSelector((state) => state.userState);
  const roleType = userState?.role?.type || userState?.roles?.[0]?.type;
  const timeZone = useSelector((state) => state.userState.company?.timeZone) ?? "No timezone available";

  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationsAnchorEl, setNotificationsAnchorEl] = useState(null);
  const [companyNotifications, setCompanyNotifications] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [isAvatarLibraryOpen, setIsAvatarLibraryOpen] = useState(false);
  const [isCompanyModalOpen, setIsCompanyModalOpen] = useState(false);

  const hasCompanies = (companies ?? []).length > 0;
  const canSwitchCompany = (companies ?? []).length > 1;

  const userShortName = `${userState?.firstName?.split(" ")[0] ?? ""} ${userState?.lastName?.slice(0, 1) ?? ""}.`.trim();
  const topLabel = currentCompany?.businessName ?? userShortName;
  const subLabel = hasCompanies ? userShortName : (userState?.roles?.[0]?.name ?? "");
  const activeRole = formatRoleLabel(currentCompany?.role);
  const initial = topLabel?.charAt(0)?.toUpperCase() ?? "?";
  const visibleCompanyNotifications = useMemo(() => companyNotifications.filter((notification) => !notification.isRead), [companyNotifications]);
  const notificationReturnState = getNotificationReturnState();
  const shouldShowReturnButton = Boolean(
    isExpanded && notificationReturnState?.companyId && String(notificationReturnState.companyId) !== String(activeCompanyId),
  );
  const returnCompanyMembership = useMemo(
    () =>
      companies.find(
        (membership) => String(membership?.companyId ?? membership?.company?.id ?? membership?.id) === String(notificationReturnState?.companyId),
      ),
    [companies, notificationReturnState?.companyId],
  );
  const returnCompanyName =
    returnCompanyMembership?.company?.businessName ||
    returnCompanyMembership?.company?.name ||
    returnCompanyMembership?.businessName ||
    "previous company";

  const handleOpenMenu = (e) => {
    if (e.target.closest?.('[data-company-avatar-editor="true"]')) return;
    if (canSwitchCompany) setAnchorEl(e.currentTarget);
  };

  const handleCloseMenu = () => setAnchorEl(null);
  const handleCloseNotifications = () => setNotificationsAnchorEl(null);

  const fetchCompanyNotifications = async () => {
    if (!activeCompanyId) return;

    setLoadingNotifications(true);

    try {
      const res = await getMyNotifications(sessionStorage.getItem("token"), 1, 3, { companyId: Number(activeCompanyId) });
      setCompanyNotifications(res?.items ?? []);
    } catch (error) {
      console.error("Error fetching company notifications:", error);
    } finally {
      setLoadingNotifications(false);
    }
  };

  const handleOpenNotifications = async (event) => {
    event.stopPropagation();

    if (notificationsAnchorEl) {
      handleCloseNotifications();
      return;
    }

    setNotificationsAnchorEl(event.currentTarget);
    await fetchCompanyNotifications();

    if (!notificationsCount || !activeCompanyId) return;

    try {
      await markAsSeenAllMyNotifications(sessionStorage.getItem("token"), { companyId: Number(activeCompanyId) });
      await queryClient.invalidateQueries({ queryKey: NOTIFICATION_COUNT_BY_COMPANY_QUERY_KEY });
    } catch (error) {
      console.error("Error marking company notifications as seen:", error);
    }
  };

  const handleMarkAsRead = async (notificationId, event) => {
    event?.stopPropagation();

    const previousNotifications = companyNotifications;
    setCompanyNotifications((items) =>
      items.map((notification) => (notification.id === notificationId ? { ...notification, isRead: true } : notification)),
    );

    try {
      await updateMyNotificatioById(sessionStorage.getItem("token"), notificationId, { isRead: true });
      await queryClient.invalidateQueries({ queryKey: NOTIFICATION_COUNT_BY_COMPANY_QUERY_KEY });
    } catch (error) {
      console.error("Error marking company notification as read:", error);
      setCompanyNotifications(previousNotifications);
    }
  };

  const ensureNotificationCompany = async (notification) => {
    const notificationCompanyId = getNotificationCompanyId(notification);
    if (!notificationCompanyId || String(notificationCompanyId) === String(activeCompanyId)) return;

    await switchActiveCompany(notificationCompanyId);
  };

  const getNotificationCompanyName = (notification) => {
    const notificationCompanyId = getNotificationCompanyId(notification);
    const membership = companies.find((item) => String(item?.companyId ?? item?.company?.id ?? item?.id) === String(notificationCompanyId));
    return membership?.company?.businessName || membership?.company?.name || membership?.businessName || membership?.name || "";
  };

  const shouldSwitchNotificationCompany = (notification) => {
    const notificationCompanyId = getNotificationCompanyId(notification);
    return Boolean(notificationCompanyId && String(notificationCompanyId) !== String(activeCompanyId));
  };

  const openNotification = async (notification) => {
    const target = getNotificationTarget(notification);

    if (target?.kind === ENotificationTargetKind.AdminInvitation) return;

    try {
      await ensureNotificationCompany(notification);

      if (target && triggerNotificationTarget(target)) {
        await handleMarkAsRead(notification.id);
        handleCloseNotifications();
        return;
      }

      const chatTarget = getNotificationChatTarget(notification);
      if (chatTarget && chatDock) {
        openCalls();
        chatDock.openChat(chatTarget, getNotificationChatMeta(notification, companies));
        await handleMarkAsRead(notification.id);
        handleCloseNotifications();
      }
    } catch (error) {
      console.error("Error opening company notification:", error);
    }
  };

  const handleNotificationAction = async (notification, event) => {
    event?.preventDefault();
    event?.stopPropagation();

    if (shouldSwitchNotificationCompany(notification)) {
      const companyName = getNotificationCompanyName(notification) || "another company";

      showDialog({
        type: "confirm",
        title: "Switch company?",
        message: `You are about to switch to ${companyName} to open this notification. Are you sure?`,
        confirmLabel: "Yes, switch",
        cancelLabel: "Cancel",
        onCancel: () => {},
        onConfirm: () => {
          saveNotificationReturnState({
            companyId: activeCompanyId,
            pathname: window.location.pathname,
            search: window.location.search,
            hash: window.location.hash,
            modalKeys: getModalKeysByStackOrder(modals),
          });
          openNotification(notification);
        },
      });
      return;
    }

    openNotification(notification);
  };

  const handleSelectCompany = async (selectedCompanyId) => {
    try {
      handleCloseMenu();
      await switchActiveCompany(selectedCompanyId);

      const returnState = consumeNotificationReturnState(selectedCompanyId);
      if (returnState) {
        navigate(`${returnState.pathname}${returnState.search}${returnState.hash}`, { replace: true });
      }
    } catch (error) {
      console.error("Error switching company:", error);
    }
  };

  const handleOpenOrganization = (event) => {
    if (event?.target?.closest?.('[data-company-avatar-editor="true"]')) return;

    const basePath = getBasePath(roleType);
    if (basePath !== "/signin") {
      navigate(`${basePath}/organization`);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        mb: 1,
        px: 1.5,
        mt: 2,
        ...sx,
        pb: 0,
      }}
    >
      <Tooltip
        open={isAvatarLibraryOpen ? false : undefined}
        disableHoverListener={isAvatarLibraryOpen}
        disableFocusListener={isAvatarLibraryOpen}
        disableTouchListener={isAvatarLibraryOpen}
        title={
          <>
            <Typography variant="body2" fontWeight={700}>
              Company Profile
            </Typography>

            <Typography variant="body3">{currentCompany?.businessName}</Typography>
          </>
        }
        sx={{
          backgroundColor: "transparent",
        }}
      >
        <Box
          onClick={handleOpenOrganization}
          sx={{
            px: isExpanded ? 1.5 : 0,
            py: isExpanded ? 1 : 0,
            borderRadius: isExpanded ? "16px" : "50%",
            border: "1px solid rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.06)",
            background: "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.95)",
            display: "flex",
            alignItems: "center",
            justifyContent: isExpanded ? "flex-start" : "center",
            gap: 1.5,
            width: isExpanded ? "100%" : 48,
            height: isExpanded ? "auto" : 48,
            cursor: canSwitchCompany ? "pointer" : "default",
            transition: "all .3s cubic-bezier(0.4, 0, 0.2, 1)",
            overflow: "visible",
            mx: "auto",

            "&:hover": {
              background: canSwitchCompany ? "var(--fx-surface-base, #FFFFFF)" : "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.95)",
              borderColor: "#fed7aa",
              boxShadow: "none",
            },
          }}
        >
          <EditableCompanyAvatar company={currentCompany} size={45} onOpenLibrary={handleCloseMenu} onLibraryOpenChange={setIsAvatarLibraryOpen} />

          {isExpanded && (
            <>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  lineHeight: 1.1,
                  minWidth: 0,
                  flex: 1,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 950,
                    fontSize: 14,
                    color: colors.gray.base,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {topLabel}
                </Typography>

                {!!subLabel && (
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: 11,
                      color: timeZone ? "#e07d00" : "gray",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {activeRole || "No role"}
                  </Typography>
                )}
              </Box>

              <Box
                component="button"
                type="button"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();

                  if (canSwitchCompany) {
                    setAnchorEl(event.currentTarget);
                  }
                }}
                sx={{
                  width: 25,
                  height: 25,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  border: 0,
                  padding: 0,
                  background: "transparent",
                  borderRadius: "8px",
                  cursor: canSwitchCompany ? "pointer" : "default",
                  opacity: canSwitchCompany ? 1 : 0,

                  "&:hover": {
                    backgroundColor: canSwitchCompany ? "rgba(251,142,0,0.08)" : "transparent",
                  },
                }}
              >
                <KeyboardArrowDown
                  sx={{
                    fontSize: 20,
                    color: colors.gray?.[400] ?? "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.45)",
                  }}
                />
              </Box>

              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  height: 28,
                  alignSelf: "center",
                  borderColor: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.10)",
                  flexShrink: 0,
                }}
              />

              <IconButton
                onClick={handleOpenNotifications}
                sx={{
                  position: "relative",
                  width: 36,
                  height: 36,
                  flexShrink: 0,
                  overflow: "visible",
                  backgroundColor: "var(--fx-surface-base, #FFFFFF)",

                  "&:hover": {
                    backgroundColor: "var(--fx-surface-sunken, #F8FAFC)",
                  },
                }}
              >
                <Notifications
                  sx={{
                    fontSize: 21,
                    color: notificationsCount > 0 ? colors.orange.base : "#78716c",
                  }}
                />

                {notificationsCount > 0 && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: -5,
                      right: -5,
                      zIndex: 10,
                      minWidth: 18,
                      height: 18,
                      px: 0.4,
                      borderRadius: "999px",
                      border: "2px solid var(--fx-surface-base, #FFFFFF)",
                      backgroundColor: "#ef4444",
                      color: "#fff",
                      fontSize: 8,
                      fontWeight: 700,
                      lineHeight: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {notificationsCount > 99 ? "99+" : notificationsCount}
                  </Box>
                )}
              </IconButton>
            </>
          )}
        </Box>
      </Tooltip>

      {shouldShowReturnButton && (
        <Button
          size="small"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            handleSelectCompany(notificationReturnState.companyId);
          }}
          disabled={isSwitchingCompany}
          sx={{
            mt: 1,
            width: "100%",
            minHeight: 38,
            justifyContent: "flex-start",
            borderRadius: "8px",
            backgroundColor: "var(--fx-surface-base, #FFFFFF)",
            color: colors.gray.base,
            textTransform: "none",
            fontSize: 12,
            fontWeight: 900,
            px: 0.75,
            overflow: "hidden",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "var(--fx-surface-sunken, #F8FAFC)",
              boxShadow: "none",
            },
          }}
        >
          <Box
            component="span"
            sx={{
              width: 28,
              height: 28,
              mr: 1,
              flexShrink: 0,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              color: colors.gray?.[500] ?? "var(--fx-surface-textsecondary, #64748B)",
            }}
          >
            <KeyboardReturnRounded sx={{ fontSize: 18, transform: "scaleX(-1)" }} />
          </Box>
          <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            Return to {returnCompanyName}
          </Box>
        </Button>
      )}

      <Menu
        anchorEl={notificationsAnchorEl}
        open={Boolean(notificationsAnchorEl)}
        onClose={handleCloseNotifications}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        TransitionProps={{ timeout: 0 }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              width: 420,
              maxWidth: 420,
              borderRadius: "28px",
              boxShadow: "-6px 6px 10px 0px rgba(0, 0, 0, 0.10)",
              border: "1px solid transparent",
            },
          },
        }}
      >
        <Box sx={{ minHeight: 200, px: 2.5, py: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h5" sx={{ color: colors.orange.base, fontWeight: 500 }}>
              Notifications company
            </Typography>

            <Button
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                handleCloseNotifications();
                onNotificationsClick?.(activeCompanyId);
              }}
              sx={{ color: colors.orange.base, textTransform: "none", fontWeight: 500 }}
            >
              View more
            </Button>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {loadingNotifications ? (
            <Box sx={{ minHeight: 120, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <CircularProgress size={22} sx={{ color: colors.orange.base }} />
            </Box>
          ) : visibleCompanyNotifications.length > 0 ? (
            visibleCompanyNotifications.map((notification) => (
              <Box
                key={`company-notification-menu-item-${notification.id}`}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 1,
                  mb: 1,
                  p: 1,
                  borderRadius: 1,
                  opacity: notification.isRead ? 0.55 : 1,
                  cursor: hasNotificationActionFor(notification) ? "pointer" : "default",
                  "&:hover": {
                    backgroundColor: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.04)",
                  },
                }}
                onClick={(event) => {
                  if (hasNotificationActionFor(notification)) {
                    handleNotificationAction(notification, event);
                  }
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", flex: 1, minWidth: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {isInboundMessageNotification(notification) ? (
                      <CompanyAvatar company={getInboundMessageSenderCompany(notification, companies)} size={28} />
                    ) : (
                      (NOTIFICATION_ICONS[notification?.type] ?? <Payment htmlColor="#fc9700" />)
                    )}
                  </ListItemIcon>

                  <ListItemText
                    primary={getNotificationDisplayMessage(notification, companies)}
                    sx={{
                      whiteSpace: "normal",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      "& .MuiTypography-root": {
                        fontSize: "0.85rem",
                        fontWeight: notification.isRead ? 500 : 700,
                      },
                    }}
                  />
                </Box>

                <IconButton size="small" onClick={(event) => handleMarkAsRead(notification.id, event)} sx={{ color: "var(--fx-surface-texttertiary, #94A3B8)" }}>
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            ))
          ) : (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              You currently have no new notifications!
            </Typography>
          )}
        </Box>
      </Menu>

      {hasCompanies && (
        <Menu
          id="company-menu"
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleCloseMenu}
          disableScrollLock
          anchorOrigin={{
            vertical: "bottom",
            horizontal: isExpanded ? "right" : "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: isExpanded ? "right" : "left",
          }}
          slotProps={{
            paper: {
              sx: {
                borderRadius: "16px",
                border: "1px solid rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.08)",
                overflow: "hidden",
                minWidth: 320,
              },
            },
          }}
          sx={{
            zIndex: 1300,
            mt: 1,

            "& .MuiMenu-list": {
              py: 0.75,
            },

            "& .MuiMenuItem-root.Mui-focusVisible": {
              backgroundColor: "transparent",
            },

            "& .MuiMenuItem-root:focus": {
              backgroundColor: "transparent",
            },

            "& .MuiMenuItem-root.Mui-selected": {
              backgroundColor: "transparent",
            },

            "& .MuiMenuItem-root.Mui-selected:hover": {
              backgroundColor: "rgba(251,142,0,0.06)",
            },

            "& .MuiMenuItem-root:hover": {
              backgroundColor: "rgba(251,142,0,0.06)",
            },
          }}
        >
          {(companies ?? []).map((membership) => (
            <CompanyMenuItem
              key={membership.companyId}
              membership={membership}
              isActive={String(membership.companyId) === String(activeCompanyId)}
              isPending={isSwitchingCompany}
              onSelect={handleSelectCompany}
            />
          ))}
        </Menu>
      )}
      {DialogComponent}
    </Box>
  );
};
